/**
 * Scheduling API Client
 */

import { apiRequest } from '@/lib/queryClient';

// Types
export interface StaffProfile {
  id: number;
  userId: number;
  employmentType: string;
  jobRole: string;
  hourlyRate?: number;
  skills: string[];
  certifications: Array<{ name: string; issuer: string; expiryDate?: string }>;
  maxJobsPerDay: number;
  requiresSupervision: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    username: string;
    fullName: string | null;
    email: string | null;
  };
}

export interface JobSchedule {
  id: number;
  installationId: number;
  scheduledDate: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  estimatedDurationHours?: number;
  actualStartTime?: string;
  actualEndTime?: string;
  status: string;
  crewSizeRequired: number;
  rolesRequired: Array<{ role: string; count: number }>;
  priority: string;
  notes?: string;
  cancellationReason?: string;
  rescheduledFrom?: number;
  createdBy?: number;
  createdAt: string;
  updatedAt: string;
  installation?: any;
  assignments?: any[];
  conflicts?: any[];
}

export interface WeatherNote {
  id: number;
  scheduleDate: string;
  location: string;
  weatherCondition: string;
  temperatureCelsius?: number;
  windSpeedMph?: number;
  notes?: string;
  isSuitableForInstallation: boolean;
  impactReason?: string;
  enteredBy: number;
  autoFetched: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
}

// Staff API
export const staffApi = {
  getAll: () => apiRequest({ url: '/api/scheduling/staff' }),
  getById: (id: number) => apiRequest({ url: `/api/scheduling/staff/${id}` }),
  create: (data: any) => apiRequest({ method: 'POST', url: '/api/scheduling/staff', data }),
  update: (id: number, data: any) => apiRequest({ method: 'PATCH', url: `/api/scheduling/staff/${id}`, data }),
  deactivate: (id: number) => apiRequest({ method: 'DELETE', url: `/api/scheduling/staff/${id}` }),

  // Availability
  getAvailability: (staffId: number) =>
    apiRequest({ url: `/api/scheduling/staff/${staffId}/availability` }),
  setAvailability: (staffId: number, data: any) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/staff/${staffId}/availability`, data }),
  updateAvailability: (availId: number, data: any) =>
    apiRequest({ method: 'PATCH', url: `/api/scheduling/availability/${availId}`, data }),

  // Leave (admin-only for full-time staff)
  getLeave: (staffId: number) => apiRequest({ url: `/api/scheduling/staff/${staffId}/leave` }),
  addLeave: (staffId: number, data: any) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/staff/${staffId}/leave`, data }),
  updateLeave: (leaveId: number, data: any) =>
    apiRequest({ method: 'PATCH', url: `/api/scheduling/leave/${leaveId}`, data }),
  deleteLeave: (leaveId: number) =>
    apiRequest({ method: 'DELETE', url: `/api/scheduling/leave/${leaveId}` }),
};

// Leave Management API (Admin) - Simplified (no approval workflow)
export const leaveApi = {
  getAll: (params?: any) => apiRequest({ url: '/api/scheduling/leave/all', params }),
  getByStaff: (staffId: number) =>
    apiRequest({ url: '/api/scheduling/leave/all', params: { staffId } }),
  add: (staffId: number, data: any) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/staff/${staffId}/leave`, data }),
  update: (leaveId: number, data: any) =>
    apiRequest({ method: 'PATCH', url: `/api/scheduling/leave/${leaveId}`, data }),
  delete: (leaveId: number) =>
    apiRequest({ method: 'DELETE', url: `/api/scheduling/leave/${leaveId}` }),
};

// Job Scheduling API
export const schedulingApi = {
  getAll: (params?: any) => apiRequest({ url: '/api/scheduling/jobs', params }),
  getById: (id: number) => apiRequest({ url: `/api/scheduling/jobs/${id}` }),
  create: (data: any) => apiRequest({ method: 'POST', url: '/api/scheduling/jobs', data }),
  update: (id: number, data: any) =>
    apiRequest({ method: 'PATCH', url: `/api/scheduling/jobs/${id}`, data }),
  cancel: (id: number, reason?: string) =>
    apiRequest({ method: 'DELETE', url: `/api/scheduling/jobs/${id}`, data: { reason } }),
  reschedule: (id: number, data: any) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/jobs/${id}/reschedule`, data }),

  // Assignments
  assignStaff: (jobId: number, data: any) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/jobs/${jobId}/assign`, data }),
  removeStaff: (jobId: number, staffId: number) =>
    apiRequest({ method: 'DELETE', url: `/api/scheduling/jobs/${jobId}/assign/${staffId}` }),
  updateAssignment: (assignmentId: number, data: any) =>
    apiRequest({ method: 'PATCH', url: `/api/scheduling/assignments/${assignmentId}`, data }),
};

// Weather API
export const weatherApi = {
  get: (date: string) => apiRequest({ url: `/api/scheduling/weather/${date}` }),
  create: (data: any) => apiRequest({ method: 'POST', url: '/api/scheduling/weather', data }),
  update: (id: number, data: any) =>
    apiRequest({ method: 'PATCH', url: `/api/scheduling/weather/${id}`, data }),
  delete: (id: number) => apiRequest({ method: 'DELETE', url: `/api/scheduling/weather/${id}` }),
  fetch: (data: { date: string; postcode: string }) =>
    apiRequest({ method: 'POST', url: '/api/scheduling/weather/fetch', data }),
};

// Conflicts API
export const conflictsApi = {
  getAll: (params?: any) => apiRequest({ url: '/api/scheduling/conflicts', params }),
  resolve: (id: number) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/conflicts/${id}/resolve` }),
  ignore: (id: number) =>
    apiRequest({ method: 'POST', url: `/api/scheduling/conflicts/${id}/ignore` }),
};
